import type { IconButtonProps } from '@chakra-ui/react'
import { Button, Text, Link, Spinner, Stack } from '@chakra-ui/react'
import type { FC } from 'react'
import { memo } from 'react'
import { useState } from 'react'

import { PopoverArrow, PopoverBody, PopoverContent, PopoverRoot, PopoverTrigger } from '@app/components/ui/popover'
import { toaster } from '@app/components/ui/toaster'
import OrganizationsSelect from '@app/pages/strategies/components/map/organizationsSelect'
import ICON_MAP from '@app/utils/iconMap'
import { useMapCloneToOrganizationMutation } from '@graphql/queries'

type Props = Omit<IconButtonProps, 'aria-label'> & {
  strategyId: string
}

const CopyMapToOrganizationButton: FC<Props> = ({ strategyId, ...rest }) => {
  const [, copyMap] = useMapCloneToOrganizationMutation()
  const [loading, setLoading] = useState(false)

  const onChange = async (newValue: { name: string; slug: string }) => {
    setLoading(true)

    try {
      const resp = await copyMap({
        input: {
          strategyId,
          organizationId: newValue.slug
        }
      })

      const { strategyId: newStrategyId, strategyName: newStrategyName } = resp.data.mapCloneToOrganization

      toaster.create({
        title: `Map copied to ${newValue.name} organization`,
        type: 'success',
        duration: 5000,
        description: (
          <Stack gap={4}>
            <Text>
              The map {newStrategyName} has been created in the {newValue.name} organization.
            </Text>
            <Button asChild>
              <Link href={`/strategy/${newStrategyId}`} rel="onoopener noreferrer" target="_blank">
                View map
              </Link>
            </Button>
          </Stack>
        )
      })
    } catch (error) {
      toaster.create({
        title: 'Error copying map',
        type: 'error',
        description: error.message
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <PopoverRoot closeOnInteractOutside={false} lazyMount>
      <PopoverTrigger asChild>
        <Button
          aria-label="Copy map to Another Organization"
          {...rest}
          colorPalette="gray"
          size="2xs"
          variant="ghost"
          loading={loading}
        >
          {loading ? <Spinner size="xs" /> : <ICON_MAP.Copy />}
          Copy to Org
        </Button>
      </PopoverTrigger>
      <PopoverContent>
        <PopoverArrow />
        <PopoverBody>
          <OrganizationsSelect onChange={onChange} />
        </PopoverBody>
      </PopoverContent>
    </PopoverRoot>
  )
}

export default memo(CopyMapToOrganizationButton)
